
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';

interface FAQSectionProps {
  industry?: string;
}

const FAQSection: React.FC<FAQSectionProps> = ({ industry }) => {
  const { currentLanguage } = useLanguage();
  
  // Enhanced FAQ data with more industry-specific questions
  const faqs = [
    {
      question: "Wie schnell bekomme ich mein Video?",
      answer: "In der Regel ist Ihr fertiges Video innerhalb von 48 Stunden verfügbar. Bei komplexeren Projekten kann es etwas länger dauern, aber wir halten Sie stets über den Fortschritt auf dem Laufenden."
    },
    {
      question: "Was kostet ein KI-Video?",
      answer: "Die Preise für KI-Videos beginnen bei CHF 490 für ein Basis-Video. Je nach Komplexität, Länge und benötigten Anpassungen kann der Preis variieren. Kontaktieren Sie uns für ein individuelles Angebot."
    },
    {
      question: "Für welche Plattformen ist es optimiert?",
      answer: "Unsere Videos werden für alle gängigen Social-Media-Plattformen optimiert: Instagram (Feed, Reels, Stories), Facebook, TikTok, LinkedIn, YouTube Shorts und Pinterest. Wir liefern in den jeweils passenden Formaten und Spezifikationen."
    },
    {
      question: "Kann ich mein Branding einbauen?",
      answer: "Absolut! Wir integrieren Ihr Branding nahtlos in die Videos – von Farben und Logos bis hin zu Schriftarten und Ihrem individuellen Look & Feel. Dies ist ein wesentlicher Bestandteil unseres Services."
    },
    {
      question: "Wie viele Änderungen sind inbegriffen?",
      answer: "In jedem Paket sind zwei Revisionsrunden enthalten. So können wir sicherstellen, dass das Video genau Ihren Vorstellungen entspricht, bevor es finalisiert wird."
    },
    {
      question: "Habt ihr Erfahrung in meiner Branche?",
      answer: "Mit über 50 erfolgreichen Projekten haben wir Erfahrung in diversen Branchen – von E-Commerce und Gesundheitswesen bis hin zu Finanzen und Bildung. Unsere KI-Technologie kann sich schnell an die Anforderungen jeder Branche anpassen."
    },
    // Previously added questions
    {
      question: "Wie lange kann ein Video maximal sein?",
      answer: "Unsere KI-generierten Videos können zwischen 15 Sekunden und 3 Minuten lang sein. Für optimale Ergebnisse auf Social Media empfehlen wir jedoch Videos zwischen 30 und 60 Sekunden, da diese die höchsten Engagement-Raten erzielen."
    },
    {
      question: "Kann ich Sprachaufnahmen oder Voice-Over hinzufügen?",
      answer: "Ja, wir bieten professionelle Voice-Over in verschiedenen Sprachen an. Alternativ können Sie auch eigene Audioaufnahmen bereitstellen, die wir in Ihr Video integrieren. Unsere KI kann zudem realistische synthetische Stimmen in über 20 Sprachen erzeugen."
    },
    {
      question: "Wie unterscheiden sich KI-Videos von traditionellen Videos?",
      answer: "KI-Videos bieten mehrere Vorteile: schnellere Produktionszeit (48 Stunden vs. Wochen), deutlich niedrigere Kosten (bis zu 80% günstiger), einfache Anpassung in verschiedene Formate und Sprachen, sowie die Möglichkeit, Inhalte ohne physische Dreharbeiten zu erstellen. Der visuelle Stil ist einzigartig und modernisiert Ihre Markenwahrnehmung."
    },
    // New industry-specific questions
    {
      question: "Welche Social-Media-Plattform eignet sich am besten für meine Zielgruppe?",
      answer: "Das hängt von Ihrer spezifischen Zielgruppe ab. Instagram und TikTok eignen sich hervorragend für jüngere Zielgruppen (18-35), während LinkedIn ideal für B2B und Fachkräfte ist. Facebook erreicht eine breitere, etwas ältere Zielgruppe. Wir analysieren Ihre Zielgruppe und empfehlen die optimalen Plattformen für Ihre speziellen Bedürfnisse."
    },
    {
      question: "Wie können KI-Videos meinen ROAS (Return On Ad Spend) verbessern?",
      answer: "KI-Videos steigern nachweislich den ROAS durch höhere Engagement-Raten (durchschnittlich +27%) und Conversion-Raten (bis zu +35%). Die personalisierbaren Inhalte ermöglichen zudem effizienteres A/B-Testing verschiedener Kreativ-Varianten ohne zusätzliche Produktionskosten, was zu einer kontinuierlichen Optimierung Ihrer Kampagnen führt."
    },
    {
      question: "Sind die Videos für bezahlte Werbekampagnen geeignet?",
      answer: "Definitiv! Unsere KI-Videos sind speziell für den Einsatz in bezahlten Werbekampagnen optimiert. Sie erfüllen alle technischen Anforderungen der Werbeplattformen und sind auf maximales Engagement ausgerichtet. Besonders in der ersten Sekunde fesseln sie die Aufmerksamkeit – entscheidend für niedrige Absprungraten und hohe Conversion-Werte."
    },
    {
      question: "Können KI-Videos an aktuelle Social-Media-Trends angepasst werden?",
      answer: "Ja, das ist einer der größten Vorteile. Wir überwachen kontinuierlich aktuelle Trends und können Ihre Videos schnell anpassen, um von viralen Formaten oder Stilen zu profitieren. Die Flexibilität der KI-Technologie ermöglicht es, innerhalb von Stunden auf neue Trends zu reagieren – im Gegensatz zu Wochen bei traditioneller Videoproduktion."
    },
    {
      question: "Wie werden die Videos für mobile Nutzung optimiert?",
      answer: "Alle unsere Videos werden primär für mobile Endgeräte konzipiert, da über 80% des Social-Media-Konsums auf Smartphones stattfindet. Dies beinhaltet optimierte vertikale Formate (9:16), große Text-Overlays für bessere Lesbarkeit, Front-Loading wichtiger Inhalte in den ersten 3 Sekunden und eine Mobile-First-Bildkomposition. Auf Wunsch liefern wir auch quadratische (1:1) oder horizontale (16:9) Versionen."
    },
    {
      question: "Können die Videos für mehrsprachige Kampagnen verwendet werden?",
      answer: "Absolut. Wir können Ihre Videos problemlos in verschiedene Sprachen übersetzen und lokalisieren – sowohl Text als auch Sprachausgabe. Dies ist besonders wertvoll für international tätige Unternehmen, die verschiedene Märkte bedienen. Die modulare KI-Architektur ermöglicht es, einmal erstellte Videos kostengünstig in bis zu 25 Sprachen zu adaptieren."
    }
  ];

  // Animation variants for enhanced microinteractions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="py-24 bg-[#0A0A0A] relative overflow-hidden" aria-labelledby="faq-heading">
      <div className="container mx-auto px-4">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 id="faq-heading" className="text-3xl md:text-4xl font-bold font-future text-white mb-6">
            Häufig gestellte Fragen
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-900 to-blue-900 mx-auto mb-6" aria-hidden="true"></div>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Alles, was Sie über unsere KI-Video-Services wissen müssen – 
            übersichtlich zusammengefasst.
          </p>
        </motion.div>
        
        <motion.div 
          className="max-w-3xl mx-auto bg-[#111116]/80 backdrop-blur-sm border border-purple-900/20 rounded-xl p-6 md:p-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <motion.div key={index} variants={itemVariants}>
                <AccordionItem 
                  value={`item-${index}`}
                  className="border-b border-purple-900/20 last:border-0 group"
                >
                  <AccordionTrigger 
                    className="text-white hover:text-purple-400 py-4 transition-all duration-300 text-left font-medium group-hover:translate-x-1"
                    aria-controls={`faq-content-${index}`}
                    id={`faq-trigger-${index}`}
                  >
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent 
                    className="text-gray-400 pb-6"
                    id={`faq-content-${index}`}
                    aria-labelledby={`faq-trigger-${index}`}
                  >
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
